// FiveStarReviewCard — "5★ review captured" on the deal Overview (Oct 6, 2026).
//
// TL and up can mark it here; RMs see the status read-only. Writes go through
// the deal_set_five_star_review() RPC, which re-checks the role server-side and
// writes the finance_audit_log row — a DB guard trigger blocks anyone below TL
// from flipping it directly.
//
// Oct 19, 2026 — the 5-Star Review SPIFF (lib/weeklySpiffs). A verified review
// is recognized with the weekly SPIFFs, and the commission point it unlocks on
// a new-structure deal pays as a SPIFF in the Tuesday run instead of inside the
// commission (Greg: "keep the same pay out"). Reviews verified before Oct 19
// keep the point in the commission. The amount per rep comes from the
// five_star_review_credits() RPC, which fills it only for reps whose pay this
// viewer may see (self, TL team, ASM downline, finance tier) — everyone else
// sees the star and no dollars. Booked / paid state comes from the spiffs rows
// tied to this deal (the same visibility rule applies to them in RLS). A review
// marked by Monday noon ET pays in that Tuesday's run with the week just
// finished (reviewPayWeek — Greg: "if chad approves by noon on Monday, they
// will get paid same week with the spiffs from prior week").

import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
import type { Deal, TeamMember } from "../../lib/types";
import { fetchReviewCredits } from "../crew/crewQueries";
import {
  addDaysISO,
  etDate,
  FIVE_STAR_REVIEW_SPIFF,
  mondayOf,
  reviewPayWeek,
  reviewReasonText,
  type ReviewCreditRow,
} from "../../lib/weeklySpiffs";

interface BookedRow { user_id: string; amount_cents: number; claimed_at: string; paid_at: string | null }

const fmtMoney = (cents: number) =>
  `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDay = (iso: string) =>
  new Date(iso.length <= 10 ? `${iso}T12:00:00Z` : iso).toLocaleDateString(undefined, {
    timeZone: iso.length <= 10 ? "UTC" : "America/New_York",
    weekday: "short",
    month: "short",
    day: "numeric",
  });
const fmtMonthDay = (iso: string) =>
  new Date(`${iso.slice(0, 10)}T12:00:00Z`).toLocaleDateString(undefined, { timeZone: "UTC", month: "short", day: "numeric" });

// Mirror of can_mark_five_star_review() in the DB — change both together.
const CAN_MARK_ROLES = new Set([
  "team_lead", "asm", "sales_director", "admin",
  "director_of_operations", "roof_director", "finance",
]);

export default function FiveStarReviewCard({
  deal, role, isSuperUser, members, onChanged,
}: {
  deal: Deal;
  role: string | null | undefined;
  isSuperUser: boolean;
  members: Record<string, TeamMember>;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // null = loading or couldn't read
  const [credits, setCredits] = useState<ReviewCreditRow[] | null>(null);
  const [creditsFailed, setCreditsFailed] = useState(false);
  const [booked, setBooked] = useState<BookedRow[] | null>(null);

  const canMark = isSuperUser || (!!role && CAN_MARK_ROLES.has(role));
  const verified = deal.five_star_review_verified === true;
  const verifiedAt = deal.five_star_review_verified_at;
  const byName = deal.five_star_review_verified_by
    ? members[deal.five_star_review_verified_by]?.full_name ?? null
    : null;
  const when = verifiedAt
    ? new Date(verifiedAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : null;
  // Reviews verified before the SPIFF started keep the point in the commission.
  const beforeStart = !verifiedAt || Date.parse(verifiedAt) < Date.parse(FIVE_STAR_REVIEW_SPIFF.startsAt);
  const reviewDay = verifiedAt ? etDate(new Date(verifiedAt)) : null;

  useEffect(() => {
    if (!verified || beforeStart || !reviewDay) {
      setCredits(null);
      setBooked(null);
      return;
    }
    let cancelled = false;
    setCreditsFailed(false);
    fetchReviewCredits(reviewDay, reviewDay).then(
      (rows) => { if (!cancelled) setCredits(rows.filter((r) => r.deal_id === deal.id)); },
      () => { if (!cancelled) { setCredits(null); setCreditsFailed(true); } },
    );
    supabase
      .from("spiffs")
      .select("user_id, amount_cents, claimed_at, paid_at")
      .eq("deal_id", deal.id)
      .eq("name", FIVE_STAR_REVIEW_SPIFF.name)
      .then(({ data, error }) => {
        if (!cancelled) setBooked(error ? null : ((data ?? []) as BookedRow[]));
      });
    return () => { cancelled = true; };
  }, [deal.id, verified, beforeStart, reviewDay]);

  // RMs only see it once it's been captured — no empty box on every deal.
  if (!canMark && !verified) return null;

  const nameOf = (id: string) => members[id]?.full_name?.split(" ")[0] ?? "Rep";
  // The Monday run (12:05 PM ET, Oct 27, 2026) right after the review's pay week (Monday noon to Monday noon)
  // books it; Alex approves it that afternoon and Shannon pays it Tuesday.
  const reviewWeek = verifiedAt ? reviewPayWeek(verifiedAt) : null;
  const booksOn = reviewWeek ? addDaysISO(reviewWeek, 7) : null;
  const runPassed = !!booksOn && booksOn < etDate(new Date());

  // Only rows whose amount this viewer may see.
  const visible = (credits ?? []).filter((c) => c.amount_cents !== null);
  const paying = visible.filter((c) => (c.amount_cents ?? 0) > 0);
  const notPaying = visible.filter((c) => (c.amount_cents ?? 0) <= 0);
  const bookedBy = new Map((booked ?? []).map((b) => [b.user_id, b] as const));
  const allBooked = paying.length > 0 && paying.every((c) => bookedBy.has(c.user_id));
  const allPaid = allBooked && paying.every((c) => !!bookedBy.get(c.user_id)?.paid_at);
  const lastPaid = allPaid
    ? paying.map((c) => bookedBy.get(c.user_id)!.paid_at!).sort().slice(-1)[0]
    : null;
  const payeeText = paying
    .map((c) => `${fmtMoney(bookedBy.get(c.user_id)?.amount_cents ?? c.amount_cents ?? 0)} ${nameOf(c.user_id)}`)
    .join(" · ");

  const lines: string[] = [];
  if (verified) {
    if (beforeStart) {
      lines.push(
        `Reviewed before the ${FIVE_STAR_REVIEW_SPIFF.name} SPIFF started on ${fmtMonthDay(FIVE_STAR_REVIEW_SPIFF.startsWeekOf)} — the 5★ point pays inside the commission.`,
      );
    } else {
      lines.push(`Recognized with the weekly SPIFFs (week of ${fmtMonthDay(reviewWeek ?? FIVE_STAR_REVIEW_SPIFF.startsWeekOf)}).`);
      if (paying.length > 0) {
        const head = `${FIVE_STAR_REVIEW_SPIFF.name} SPIFF (the 5★ commission point): ${payeeText}`;
        if (allBooked) {
          const firstBooked = bookedBy.get(paying[0].user_id)!;
          lines.push(
            `${head} — booked with the week of ${fmtMonthDay(mondayOf(new Date(firstBooked.claimed_at)))}${
              allPaid && lastPaid ? ` · paid ${fmtDay(lastPaid)} ✓` : " · awaiting payout"
            }`,
          );
        } else if (booked !== null && runPassed && booksOn) {
          lines.push(`${head} — should have booked ${fmtDay(booksOn)}; check Finance → Spiffs`);
        } else {
          lines.push(`${head} — books ${booksOn ? fmtDay(booksOn) : "Monday"} with the weekly SPIFFs`);
        }
      }
      for (const c of notPaying) {
        lines.push(`No SPIFF for ${nameOf(c.user_id)}: ${reviewReasonText(c.reason) ?? "no SPIFF"}.`);
      }
      if (creditsFailed) lines.push("Couldn't load the SPIFF amount — reload to try again.");
    }
  }

  async function setVerified(next: boolean) {
    setBusy(true);
    setErr(null);
    const { error } = await supabase.rpc("deal_set_five_star_review", {
      p_deal_id: deal.id,
      p_verified: next,
    });
    setBusy(false);
    setConfirmClear(false);
    if (error) { setErr(formatSupabaseError(error)); return; }
    onChanged();
  }

  if (verified) {
    return (
      <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 flex flex-wrap items-center gap-3">
        <span className="text-lg" aria-hidden>⭐</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-amber-900">5★ review captured</div>
          <div className="text-xs text-amber-800">
            {[byName && `by ${byName}`, when].filter(Boolean).join(" · ") || "Verified"}
          </div>
          {lines.map((l) => (
            <div key={l} className="text-xs text-amber-900 mt-0.5">⭐ {l}</div>
          ))}
          {err && <div className="text-xs text-rose-700 mt-1">{err}</div>}
        </div>
        {canMark && !confirmClear && (
          <button
            type="button"
            onClick={() => setConfirmClear(true)}
            className="text-xs text-amber-800 underline hover:text-amber-950"
          >
            Undo
          </button>
        )}
        {canMark && confirmClear && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-amber-900">
              Remove the 5★ review?
              {(booked?.length ?? 0) > 0 && " Its SPIFF is already booked — removing the review won't unbook it."}
            </span>
            <button
              type="button"
              disabled={busy}
              onClick={() => setVerified(false)}
              className="text-xs px-2 py-1 rounded bg-rose-600 text-white disabled:opacity-50"
            >
              {busy ? "…" : "Remove"}
            </button>
            <button
              type="button"
              onClick={() => setConfirmClear(false)}
              className="text-xs px-2 py-1 rounded border border-amber-300 text-amber-900"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex flex-wrap items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-800">5★ review</div>
        <div className="text-xs text-slate-500">
          Homeowner left a 5-star review? Mark it here. It's recognized with the weekly SPIFFs, and on a qualifying
          job the 5★ commission point pays as a {FIVE_STAR_REVIEW_SPIFF.name} SPIFF — marked by Monday noon, it books
          that afternoon with the week just finished and is paid Tuesday.
        </div>
        {err && <div className="text-xs text-rose-700 mt-1">{err}</div>}
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={() => setVerified(true)}
        className="px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold disabled:opacity-50"
      >
        {busy ? "Saving…" : "⭐ 5★ review captured"}
      </button>
    </div>
  );
}
